import React, { useEffect, useState, useCallback } from "react";
import { Search, Download, RefreshCw } from "lucide-react";

const API     = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card    = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls= "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls= "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const MODULES  = ["All","Users","Leave","Attendance","Payroll","Performance","Tickets","Projects","Documents","System"];
const ACTIONS  = ["All","CREATE","UPDATE","DELETE","APPROVE","REJECT","LOGIN","EXPORT","IMPORT"];

const ACTION_STYLE = {
  CREATE:  "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  UPDATE:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  DELETE:  "text-red-400 bg-red-500/10 border-red-500/20",
  APPROVE: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  REJECT:  "text-red-400 bg-red-500/10 border-red-500/20",
  LOGIN:   "text-white/40 bg-white/5 border-white/10",
  EXPORT:  "text-purple-400 bg-purple-500/10 border-purple-500/20",
  IMPORT:  "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
};

export default function AuditLogTab() {
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [page,      setPage]      = useState(1);
  const [hasMore,   setHasMore]   = useState(true);
  const [total,     setTotal]     = useState(0);
  const [search,    setSearch]    = useState("");
  const [module,    setModule]    = useState("All");
  const [action,    setAction]    = useState("All");
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");
  const [exporting, setExporting] = useState(false);
  const token = localStorage.getItem("token");
  const PAGE_SIZE = 50;

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    const p = reset ? 1 : page;
    try {
      const qs = new URLSearchParams({
        page: p, limit: PAGE_SIZE,
        ...(search  ? { q: search }        : {}),
        ...(module !== "All" ? { module }  : {}),
        ...(action !== "All" ? { action }  : {}),
        ...(dateFrom ? { from: dateFrom }  : {}),
        ...(dateTo   ? { to: dateTo }      : {}),
      }).toString();
      const res = await fetch(`${API}/api/admin/audit-log?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const items = Array.isArray(data) ? data : data?.items || [];
      const tot   = data?.total ?? items.length;
      setTotal(tot);
      setLogs(reset || p === 1 ? items : prev => [...prev, ...items]);
      setHasMore(items.length === PAGE_SIZE);
      if (reset) setPage(1);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  }, [page, search, module, action, dateFrom, dateTo]);

  useEffect(() => { load(true); }, [search, module, action, dateFrom, dateTo]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ format: "csv", ...(module !== "All" ? { module } : {}), ...(action !== "All" ? { action } : {}) }).toString();
      const res = await fetch(`${API}/api/admin/audit-log/export?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `audit_log_${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {} finally { setExporting(false); }
  };

  return (
    <div className="space-y-5">

      {/* Filters */}
      <div className={`${card} p-5`}>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search size={13} className="text-[#00ff90]/40 shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search actor, resource, details…"
              className="flex-1 bg-transparent text-sm text-white outline-none placeholder-white/20" />
          </div>
          <div>
            <label className={labelCls}>Module</label>
            <select className={inputCls} value={module} onChange={e => setModule(e.target.value)}>
              {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Action</label>
            <select className={inputCls} value={action} onChange={e => setAction(e.target.value)}>
              {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>From</label>
            <input type="date" className={inputCls} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>To</label>
            <input type="date" className={inputCls} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <button onClick={() => load(true)} disabled={loading}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors self-end">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={exportCsv} disabled={exporting}
            className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors self-end">
            <Download size={12} /> Export
          </button>
        </div>
      </div>

      {/* Log table */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Audit Log</span>
          <span className="text-xs text-white/30">{total.toLocaleString()} entries</span>
        </div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0d0d0d]">
              <tr className="border-b border-white/5">
                {["Timestamp","Actor","Action","Module","Resource","Details","IP"].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && logs.length === 0 ? (
                Array.from({length:8}).map((_,i) => (
                  <tr key={i}><td colSpan={7} className="px-3 py-3"><div className="h-6 rounded bg-white/5 animate-pulse" /></td></tr>
                ))
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-white/20 italic text-sm">No audit logs found.</td></tr>
              ) : logs.map((log, i) => {
                const actionStyle = ACTION_STYLE[log.action] || ACTION_STYLE.LOGIN;
                return (
                  <tr key={log.id || i} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                    <td className="px-3 py-2.5 text-xs text-white/30 whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }) : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="text-xs text-white/70 font-medium">{log.actor_name || "—"}</p>
                      <p className="text-xs text-white/30">{log.actor_role}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${actionStyle}`}>{log.action}</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-white/40">{log.module}</td>
                    <td className="px-3 py-2.5 text-xs text-white/50">{log.resource_type}{log.resource_id ? ` #${String(log.resource_id).slice(0,8)}` : ""}</td>
                    <td className="px-3 py-2.5 text-xs text-white/30 max-w-xs truncate">{log.details || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-white/25 font-mono">{log.ip_address || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="px-5 py-4 border-t border-white/5 text-center">
            <button onClick={() => { setPage(p => p + 1); load(); }} disabled={loading}
              className="text-xs px-5 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white disabled:opacity-40 transition-colors">
              {loading ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}